import React, { useState, useEffect } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Shield, ShieldCheck } from "lucide-react";

export default function SupportRoles() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", full_access: true });

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/support-roles"); setItems(data); } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", description: "", full_access: true }); setDlg(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, description: r.description || "", full_access: !!r.full_access }); setDlg(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("الاسم مطلوب");
    try {
      if (editing) await api.put(`/support-roles/${editing.id}`, form);
      else await api.post("/support-roles", form);
      toast.success(editing ? "تم التحديث" : "تم إنشاء الدور");
      setDlg(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (r) => {
    if (!window.confirm(`حذف دور "${r.name}"؟`)) return;
    try { await api.delete(`/support-roles/${r.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-4" data-testid="support-roles-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-primary" /> أدوار الدعم المخصصة</h1>
          <p className="text-sm text-muted-foreground mt-1">أنشئ قوالب أدوار لموظفي دعم شركتك (محاسب دعم، دعم فني، مشرف مبيعات...)</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-role-btn"><Plus className="w-4 h-4" /> دور جديد</Button>
      </div>

      {loading ? <div className="text-center text-muted-foreground py-12">جاري التحميل...</div> :
       items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-lg text-muted-foreground">
          <Shield className="w-16 h-16 mx-auto opacity-30 mb-3" />
          <div>لا توجد أدوار حتى الآن</div>
          <Button variant="link" onClick={openNew} className="mt-2">أنشئ الدور الأول</Button>
        </div>
       ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(r => (
            <div key={r.id} className="border rounded-lg p-4 bg-card hover:border-primary/40 transition" data-testid={`role-${r.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded bg-primary/10 grid place-items-center"><ShieldCheck className="w-5 h-5 text-primary" /></div>
                  <div>
                    <div className="font-bold">{r.name}</div>
                    {r.full_access && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">صلاحيات كاملة</span>}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(r)}><Edit2 className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(r)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              {r.description && <p className="text-xs text-muted-foreground line-clamp-3">{r.description}</p>}
            </div>
          ))}
        </div>
       )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="role-dialog">
          <DialogHeader><DialogTitle>{editing ? "تعديل الدور" : "دور جديد"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>اسم الدور</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="محاسب دعم" data-testid="role-name" />
            </div>
            <div>
              <Label>الوصف</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="وصف مسؤوليات هذا الدور..." />
            </div>
            <div className="flex items-center justify-between p-3 rounded-md border bg-muted/30">
              <div>
                <div className="font-medium text-sm">صلاحيات كاملة</div>
                <div className="text-xs text-muted-foreground">يعمل كأنه مدير للشركة العميلة (مثل company_manager)</div>
              </div>
              <Switch checked={form.full_access} onCheckedChange={(v) => setForm({ ...form, full_access: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button>
            <Button onClick={save} data-testid="save-role-btn">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
